/**
 * Guardian's view model: a port of the design's gdLayer (Aithora ConsoleV3.dc.html
 * L34240-34452) over the live data instead of its sample seed. Pure: the page passes the
 * API answers, the role and the clock, and gets back every value the template draws.
 *
 * Where a case comes from:
 *   - Guardian signals (GET /guardian/signals): abuse or neglect, money pressure, injury,
 *     crisis, and the rest of GUARDIAN_KINDS;
 *   - emergencies (GET /emergency/incidents): the design's kind 'emergency';
 *   - early-warning trends (GET /guardian/watch): the design's kind 'watch'.
 *
 * The reporting clock is the server's (deadline.deadlineAt, from deadlineFor). The two
 * clocks the server does not compute yet are named rules here: the crisis protocol's
 * 4-hour reassessment and the 5 working days to findings after a report.
 */
import { compareEmergencies, type GuardianIncident, type GuardianSignal, type GuardianStep, type GuardianTapeEvent, type GuardianWatchItem } from '@aithora/shared';
import { emergencyAge } from '../../shell/EmergencyBanner';

/** Crisis protocol: reassess within 4 hours of a positive screen (design clock()). */
export const CRISIS_REASSESS_HOURS = 4;
/** Findings after a report go to the state within 5 working days (design clock()). */
export const FINDINGS_WORKING_DAYS = 5;
/** Someone should be with a resident within 5 minutes of an emergency alert. */
export const EMERGENCY_BAR_MS = 300_000;
/** Aithora Home: the care line promises a person within 10 minutes, text first. */
export const HOME_CARE_LINE_MS = 600_000;

export type Lens = 'staff' | 'admin' | 'ro';
export type Family = 'report' | 'crisis' | 'emergency' | 'watch' | 'home';
export type Filter = 'open' | 'mine' | 'unowned' | 'watch' | 'closed';

/** The design's KIND table, plus labels for the live kinds it has no word for. */
const KIND_LABEL: Record<string, string> = {
  watch: 'Early warning', emergency: 'Emergency', abuse: 'Possible abuse', neglect: 'Possible neglect', abuse_neglect: 'Possible abuse or neglect',
  money: 'Money pressure', injury: 'Injury, cause unknown', crisis: 'Crisis', other: 'Concern',
  fall: 'Fall', missing_resident: 'Missing resident', medication_error: 'Medication error', chest_pain: 'Chest pain',
};

export const STAGES: Record<Family, [string, string][]> = {
  report: [['new', 'New'], ['claimed', 'Claimed'], ['ack', 'Suspicion confirmed'], ['reported', 'Reported'], ['investigating', 'Investigating'], ['closed', 'Closed']],
  crisis: [['new', 'New'], ['claimed', 'Claimed'], ['screened', 'Screened'], ['reassessed', 'Reassessed'], ['closed', 'Closed']],
  emergency: [['new', 'Alert'], ['claimed', 'On the way'], ['onscene', 'With them'], ['closed', 'Resolved']],
  watch: [['watching', 'Watching']],
  home: [['new', 'New'], ['claimed', 'Claimed'], ['contacted', 'Reached her'], ['closed', 'Closed']],
};

const NEXT: Partial<Record<Family, Record<string, string>>> = {
  report: { new: 'Claim', claimed: 'Confirm suspicion', ack: 'File report', reported: 'Start investigation', investigating: 'Close with findings' },
  crisis: { new: 'Claim', claimed: 'Record screen', screened: 'Record reassessment', reassessed: 'Close with safety plan' },
  emergency: { new: 'I’m going', claimed: 'I’m with them', onscene: 'Resolved' },
  home: { new: 'Claim', claimed: 'I reached her', contacted: 'Close' },
};

/** The server step each "next" button sends on a signal (report_filed goes through the form). */
export const NEXT_STEP: Partial<Record<Family, Record<string, GuardianStep>>> = {
  report: { new: 'claim', claimed: 'confirm_suspicion', reported: 'investigation_started', investigating: 'closed' },
  crisis: { new: 'claim', claimed: 'screened', screened: 'reassessed', reassessed: 'closed' },
  // Aithora Home: the server moves a household case to 'contacted' on going_now ("I reached her").
  home: { new: 'claim', claimed: 'going_now', contacted: 'closed' },
};

/** What each step reads as in the log (the design's words for the same moves). */
const STEP_WORDS: Record<string, string> = {
  claim: 'Claimed', going_now: 'Going to them now', report_filed: 'Report filed', closed: 'Closed with findings',
  confirm_suspicion: 'Confirmed a reasonable suspicion', investigation_started: 'Investigation started', screened: 'Columbia screen recorded',
  reassessed: 'Reassessed, safety plan updated', escalated: 'Escalated to the Director of Nursing',
  // An emergency's steps (the server's names; Console V6 words).
  going: 'Going to them now', going_undone: 'Undid “I’m going”', with_them: 'With them', called_911: 'Called 911', ems_arrived: 'EMS arrived', resolved: 'Resolved on scene',
};

/**
 * A step in plain words: the design's word for it, or the server's name with its
 * underscores read out ("family_told" reads "Family told"). Never the raw name.
 */
export function stepWord(step: string): string {
  const w = STEP_WORDS[step];
  if (w) return w;
  const t = step.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return t ? t[0].toUpperCase() + t.slice(1) : 'Step recorded';
}

/** The words the emergency card says, the same on the console and the phone (design emergency-card.js). */
export const EM = {
  going: 'I’m going',
  too: 'I’m going too',
  with: 'I’m with them',
  resolved: 'Resolved',
  c911: 'Called 911',
  ems: 'EMS arrived',
  undo: (s: number) => 'Undo · ' + s + 's',
  undoSeconds: 10,
  also: (n: string) => (n === 'You' ? 'You are also responding' : n + ' is also responding'),
  pagingAll: 'Paging keeps running for everyone until someone is with them. One person’s “I’m going” never quiets it on anyone else’s phone.',
  pagingNow: 'Paging everyone on shift now.',
  after911: '911 is on the way. Alerts keep running for everyone until EMS arrives.',
  failWhy: 'Aithora didn’t answer, so nothing changed and no one was told.',
  callStation: 'Call the nurse station',
};

const EDGE: Record<string, string> = {
  emergency: '#B3261E', abuse: '#B3261E', crisis: '#B3261E', neglect: '#8A5A00', money: '#8A5A00', injury: '#8A5A00', other: '#54606C',
  abuse_neglect: '#B3261E', fall: '#B3261E', chest_pain: '#B3261E', missing_resident: '#B3261E', medication_error: '#8A5A00',
};

/** One case, whatever it came from. */
export interface Case {
  id: string;
  src: 'signal' | 'incident' | 'watch';
  /** The design's kind key (abuse, neglect, money, injury, crisis, emergency, watch, other) or a live kind. */
  kind: string;
  who: string;
  room: string | null;
  bldg: string;
  type: 'SNF' | 'ALF' | 'HOME' | null;
  at: number;
  stage: string;
  owner: string | null;
  ownerId: string | null;
  what: string;
  sbi: boolean | null;
  reportedAt: number | null;
  closedAt: number | null;
  deadlineAt: number | null;
  deadlineHours: number | null;
  noDeadlineReason: string | null;
  residentId: string | null;
  escalated: boolean;
  /** Who a report goes to, from the building's state (the server's per-state agencies). */
  reportTo: { key: string; label: string; sub: string }[] | null;
  /** An emergency's kind in a word ("fall"), for "Beatrice's fall first". */
  emWord: string;
  /** An emergency's responders, first to say "I'm going" first (several can respond). */
  responders?: { id: string | null; name: string }[];
  /** An emergency's recorded "Called 911" and "EMS arrived", with who. */
  called911?: { by: string; at: number } | null;
  ems?: { by: string; at: number } | null;
  /** An emergency's fields for the shared severity order (compareEmergencies); unset otherwise. */
  emRank?: { type: string | null; status: string; severity: string | null; requires_911: boolean | null; created_at: string };
  log: { at: number; who: string; what: string; id: string }[];
}

const ms = (iso: string | null | undefined): number | null => (iso ? Date.parse(iso) : null);

/** "2:07 pm" (design hhmm). */
export function hhmm(t: number): string {
  const d = new Date(t);
  let h = d.getHours();
  const ap = h < 12 ? 'am' : 'pm';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ap}`;
}

/** The design's dur(), shared with the shell's emergency line. */
export const dur = emergencyAge;

function addWork(t: number, n: number): number {
  const d = new Date(t);
  let k = 0;
  while (k < n) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w && w < 6) k++;
  }
  return d.getTime();
}

function signalKind(s: GuardianSignal): string {
  if (s.kind === 'abuse_neglect') return s.concern === 'neglect' ? 'neglect' : s.concern === 'abuse' ? 'abuse' : 'abuse_neglect';
  if (s.kind === 'scam') return 'money';
  if (s.kind === 'unsafe_feeling' || s.kind === 'distress') return 'crisis';
  if (s.kind === 'other') return s.concern === 'injury' ? 'injury' : 'other';
  return s.kind;
}

const SIGNAL_STAGE: Record<string, string> = { open: 'new', responding: 'claimed', confirmed: 'ack' };

export function fromSignal(s: GuardianSignal): Case {
  const evidence = (s.evidence ?? []).map((e) => ({ at: Date.parse(e.at), who: e.source, what: e.text, id: e.ledgerRef ?? '–' }));
  const steps = s.steps.map((st) => ({
    at: Date.parse(st.at),
    who: st.actor.name ?? st.actor.role ?? '–',
    what: st.note ?? stepWord(st.step),
    id: st.ledgerRef ?? '–',
  }));
  return {
    id: s.id,
    src: 'signal',
    kind: signalKind(s),
    who: s.residentName ?? 'Resident not named',
    room: s.room ?? null,
    bldg: s.facilityName ?? '',
    type: s.facilityType,
    at: Date.parse(s.surfacedAt),
    stage: SIGNAL_STAGE[s.stage] ?? s.stage,
    owner: s.owner ? (s.owner.name ?? 'Someone') : null,
    ownerId: s.owner?.userId ?? null,
    what: s.summary ?? '',
    sbi: s.sbi,
    reportedAt: ms(s.reportedAt),
    closedAt: ms(s.closedAt),
    deadlineAt: ms(s.deadline.deadlineAt),
    deadlineHours: s.deadline.hours,
    noDeadlineReason: s.deadline.deadlineAt ? null : s.deadline.reason,
    residentId: s.residentId,
    escalated: !!s.escalated || s.steps.some((x) => x.step === 'escalated'),
    emWord: '',
    reportTo: s.reportTo ?? null,
    log: evidence.concat(steps),
  };
}

export function fromIncident(i: GuardianIncident): Case {
  const at = Date.parse(i.created_at);
  const closedAt = i.resolved_at ? Date.parse(i.resolved_at) : null;
  const stage = i.status === 'resolved' || closedAt ? 'closed' : i.arrived_at ? 'onscene' : i.acknowledged_at ? 'claimed' : 'new';
  const log: Case['log'] = [{ at, who: i.sourceLabel ?? 'Emergency alert', what: i.evidence ?? i.description ?? (i.type ?? 'Emergency'), id: i.ledgerRef ?? '–' }];
  // Responders, in the order they said "I'm going" (an Undo takes that person off).
  const responders: { id: string | null; name: string }[] = [];
  let called911: Case['called911'] = null;
  let ems: Case['ems'] = null;
  if (i.steps) {
    // The sealed steps, with who took each and its ledger reference, in plain words.
    for (const st of i.steps) {
      const who = st.actor.name ?? st.actor.role ?? '–';
      log.push({ at: Date.parse(st.at), who, what: st.note ?? stepWord(st.step), id: st.ledgerRef ?? '–' });
      const same = (r: { id: string | null; name: string }) => (st.actor.userId ? r.id === st.actor.userId : r.name === who);
      if (st.step === 'going' && !responders.some(same)) responders.push({ id: st.actor.userId, name: who });
      if (st.step === 'going_undone') {
        const k = responders.findIndex(same);
        if (k >= 0) responders.splice(k, 1);
      }
      if (st.step === 'called_911' && !called911) called911 = { by: who, at: Date.parse(st.at) };
      if (st.step === 'ems_arrived' && !ems) ems = { by: who, at: Date.parse(st.at) };
    }
  } else {
    if (i.acknowledged_at) log.push({ at: Date.parse(i.acknowledged_at), who: i.acknowledgedByName ?? '–', what: 'Going to them now', id: '–' });
    if (i.arrived_at) log.push({ at: Date.parse(i.arrived_at), who: i.arrivedByName ?? '–', what: 'With them', id: '–' });
    if (i.called911At) log.push({ at: Date.parse(i.called911At), who: '–', what: 'Called 911', id: '–' });
    if (i.emsArrivedAt) log.push({ at: Date.parse(i.emsArrivedAt), who: '–', what: 'EMS arrived', id: '–' });
  }
  if (responders.length === 0 && i.acknowledged_at) responders.push({ id: i.acknowledgedByUserId ?? null, name: i.acknowledgedByName ?? 'Someone' });
  if (!called911 && i.called911At) called911 = { by: 'Someone', at: Date.parse(i.called911At) };
  if (!ems && i.emsArrivedAt) ems = { by: 'Someone', at: Date.parse(i.emsArrivedAt) };
  return {
    id: i.id,
    src: 'incident',
    kind: 'emergency',
    who: i.residentName ?? 'A resident',
    room: i.room,
    bldg: i.facilityName ?? '',
    type: i.facilityType ?? null,
    at,
    stage,
    owner: i.acknowledgedByName ?? (i.acknowledged_at ? 'Someone' : null),
    ownerId: i.acknowledgedByUserId ?? null,
    what: i.description ?? '',
    sbi: null,
    reportedAt: null,
    closedAt,
    deadlineAt: null,
    deadlineHours: null,
    noDeadlineReason: null,
    residentId: i.user_id ?? null,
    escalated: false,
    reportTo: null,
    emWord: i.type ? i.type.replace(/_/g, ' ').toLowerCase() : 'emergency',
    responders,
    called911,
    ems,
    emRank: { type: i.type, status: i.status, severity: i.severity ?? null, requires_911: i.requires_911 ?? null, created_at: i.created_at },
    log,
  };
}

export function fromWatch(w: GuardianWatchItem): Case {
  return {
    id: w.id,
    src: 'watch',
    kind: 'watch',
    who: w.residentName ?? 'Resident not named',
    room: w.room,
    bldg: w.facilityName ?? '',
    type: w.facilityType,
    at: Date.parse(w.since),
    stage: 'watching',
    owner: null,
    ownerId: null,
    what: w.summary,
    sbi: null,
    reportedAt: null,
    closedAt: null,
    deadlineAt: null,
    deadlineHours: null,
    noDeadlineReason: null,
    residentId: w.residentId,
    escalated: false,
    emWord: '',
    reportTo: null,
    log: w.evidence.map((e) => ({ at: Date.parse(e.at), who: e.source, what: e.text, id: e.ledgerRef ?? '–' })),
  };
}

export function familyOf(x: Case): Family {
  if (x.type === 'HOME') return 'home';
  if (x.kind === 'watch') return 'watch';
  if (x.kind === 'emergency') return 'emergency';
  if (x.kind === 'crisis') return 'crisis';
  return 'report';
}

export interface Clock {
  left: number;
  text: string;
  color: string;
  pct: number;
  basis: string;
}

/** The design's clock(x): what the row's clock says, its colour and why. */
export function clockOf(x: Case, now: number): Clock {
  const f = familyOf(x);
  if (x.kind === 'watch') return { left: 9e11, text: 'Watching', color: '#54606C', pct: 0, basis: 'Not a signal yet. Guardian is tracking a trend. It moves to Open, with a clock, the moment it crosses its line.' };
  if (x.stage === 'closed') return { left: Infinity, text: 'Closed', color: '#54606C', pct: 0, basis: `Closed ${x.closedAt ? hhmm(x.closedAt) : 'earlier this week'}.` };
  if (f === 'emergency') {
    const e = now - x.at;
    return {
      left: -1e12, text: `${dur(e)} since alert`, color: '#B3261E', pct: Math.min(1, e / EMERGENCY_BAR_MS),
      basis: x.stage === 'new' ? 'Nobody has claimed it. Someone should be with them within 5 minutes.' : `${x.owner ?? 'Someone'}${x.stage === 'claimed' ? ' is on the way.' : ' is with them.'}`,
    };
  }
  if (f === 'crisis') {
    const span = CRISIS_REASSESS_HOURS * 3600000;
    const l = x.at + span - now;
    return {
      left: l, text: l > 0 ? `Reassess in ${dur(l)}` : `Reassessment ${dur(l)} late`, color: l < 1800000 ? '#B3261E' : '#22272E', pct: 1 - l / span,
      basis: 'Your crisis protocol: reassess within 4 hours of a positive screen, and at shift handoff. 988 is the outside handoff.',
    };
  }
  if (x.stage === 'reported' || x.stage === 'investigating') {
    const rep = x.reportedAt ?? now;
    const due = addWork(rep, FINDINGS_WORKING_DAYS);
    const l = due - now;
    return {
      left: l + 1e11, text: `Findings due ${new Date(due).toLocaleDateString('en-US', { weekday: 'short' })}`, color: '#22272E', pct: 1 - l / (7 * 86400000),
      basis: `Reported ${hhmm(rep)}, ${dur(rep - x.at)} after it reached staff. Investigation results go to the state within 5 working days.`,
    };
  }
  if (x.type === 'HOME' && x.stage === 'contacted') return { left: 1e11, text: 'Reached her', color: '#12503C', pct: 1, basis: 'The care line spoke with her. Close it once she and her family agree on what happens next.' };
  if (x.type === 'HOME') {
    const l = x.at + HOME_CARE_LINE_MS - now;
    return {
      left: l, text: l > 0 ? `Care line in ${dur(l)}` : `Care line ${dur(l)} late`, color: '#B3261E', pct: 1 - l / HOME_CARE_LINE_MS,
      basis: `At home, so no facility reporting clock applies. The care line promised a person within 10 minutes, text first. Family was told at ${hhmm(x.at)}.`,
    };
  }
  if (x.deadlineAt === null) {
    // The server computed no deadline and says why; nothing is guessed.
    const alf = x.type === 'ALF';
    return {
      left: 5e10, text: alf ? 'State rule not loaded' : 'No reporting clock', color: alf ? '#8A5A00' : '#54606C', pct: 0,
      basis: x.noDeadlineReason ?? 'No reporting clock applies to this signal.',
    };
  }
  const w = x.deadlineHours ?? (x.deadlineAt - x.at) / 3600000;
  const l = x.deadlineAt - now;
  return {
    left: l, text: l > 0 ? `${dur(l)} left to report` : `Report ${dur(l)} late`, color: l < 3600000 ? '#B3261E' : l < 6 * 3600000 ? '#8A5A00' : '#22272E', pct: 1 - l / (w * 3600000),
    basis:
      (x.sbi === false ? '24 hours: no serious bodily injury.' : x.sbi === true ? '2 hours: serious bodily injury.' : '2 hours until someone confirms there was no serious bodily injury.') +
      ` Counted from ${hhmm(x.at)}, when it reached staff, the earliest a suspicion could have formed. Real clock time, nights and weekends included.`,
  };
}

export interface GuardianInput {
  lens: Lens;
  meId: string | null;
  now: number;
  signals: GuardianSignal[];
  incidents: GuardianIncident[];
  watch: GuardianWatchItem[];
  tape: GuardianTapeEvent[];
  /** Care Staff: the residents on their shift (their floor). Null = no assignment known. */
  mine: ReadonlySet<string> | null;
  /** The building's name for rows that carry none (facility roles see one building). */
  orgName: string;
  filter: Filter;
  sel: string | null;
  formFor: string | null;
}

export interface Row {
  id: string;
  who: string;
  room: string;
  kind: string;
  what: string;
  clockText: string;
  clockColor: string;
  owner: string;
  ownerColor: string;
  stageLabel: string;
  dots: string[];
  hasNext: boolean;
  nextLabel: string;
  nextStyle: string;
  rowStyle: string;
  edgeStyle: string;
  barStyle: string;
}

/** A button (design btn(on, tone)): 44px for Care Staff, 36px otherwise. */
export function btn(staff: boolean, tone: 'red' | 'ghost' | ''): string {
  return (
    'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;min-height:' + (staff ? 44 : 36) + 'px;padding:0 ' + (staff ? 18 : 14) + 'px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;' +
    (tone === 'red' ? 'color:#fff;background:#B3261E;' : tone === 'ghost' ? 'color:#22272E;background:transparent;border:1px solid rgba(34,39,46,.22);' : 'color:#FCFAF4;background:#1D3F6B;')
  );
}

export const labelOf = (x: Case): string => KIND_LABEL[x.kind] ?? 'Concern';

/** The first responder's name ("You" for me). */
function responderName(x: Case, meId: string | null): string | null {
  const r = x.responders?.[0];
  if (!r) return null;
  return meId && r.id === meId ? 'You' : r.name;
}

/** One action on the emergency card: what it says, whether it is the red one, and which step it takes. */
export interface EmAct {
  key: 'next' | 'too' | 'undo' | 'c911' | 'ems';
  label: string;
  primary: boolean;
}

/**
 * The one emergency card (Console V6 emView, emergency-card.js), drawn in the top banner
 * (top) and in the case panel. Several people can respond; one person's "I'm going" never
 * quiets the alert for anyone else. `undoLeft` is the seconds left on my own "I'm going".
 */
export function emergencyCard(x: Case, top: boolean, opts: { meId: string | null; canAct: boolean; undoLeft: number }) {
  const resp = x.responders ?? [];
  const named = resp.map((r) => (opts.meId && r.id === opts.meId ? 'You' : r.name));
  const mine = !!opts.meId && resp.some((r) => r.id === opts.meId);
  const live = x.stage !== 'closed';
  const act = live && opts.canAct;
  const c9 = x.called911 ?? null;
  const ems = x.ems ?? null;
  const lines: { t: string; strong: boolean }[] = [];
  named.slice(1).forEach((n) => lines.push({ t: EM.also(n), strong: true }));
  if (live && x.stage !== 'onscene') lines.push({ t: c9 && !ems ? EM.after911 : resp.length ? EM.pagingAll : EM.pagingNow, strong: false });
  const steps: { t: string; w: string }[] = [];
  if (c9) steps.push({ t: EM.c911, w: c9.by + ' · ' + hhmm(c9.at) });
  if (ems) steps.push({ t: EM.ems, w: ems.by + ' · ' + hhmm(ems.at) });
  const acts: EmAct[] = [];
  if (act) {
    if (top && (x.stage === 'new' || mine)) {
      const nx = (NEXT.emergency ?? {})[x.stage];
      if (nx) acts.push({ key: 'next', label: nx, primary: true });
    }
    if (x.stage !== 'new' && !mine) acts.push({ key: 'too', label: EM.too, primary: true });
    if (opts.undoLeft > 0) acts.push({ key: 'undo', label: EM.undo(opts.undoLeft), primary: false });
    if (x.stage !== 'new' && !c9) acts.push({ key: 'c911', label: EM.c911, primary: false });
    if (c9 && !ems) acts.push({ key: 'ems', label: EM.ems, primary: false });
  }
  return { lines, steps, acts, mine };
}
export const nextLabelOf = (x: Case): string => (NEXT[familyOf(x)] ?? {})[x.stage] ?? '';

/** Every value the page draws, from the cases (design gdLayer's return). */
export function buildGuardian(inp: GuardianInput) {
  const { lens, now, meId } = inp;
  const staff = lens === 'staff';
  const ro = lens === 'ro';
  const isMe = (x: Case) => !!meId && x.ownerId === meId;
  // Aithora staff read facility cases; they own only a household's (no facility can act).
  const canAct = (x: Case) => !ro || x.type === 'HOME';
  // "I'm with them" is the claimer's own step (the server refuses anyone else), so a
  // colleague sees who is on the way, not a button that answers 403.
  const stepOk = (x: Case) => !(familyOf(x) === 'emergency' && x.stage === 'claimed' && !isMe(x));
  const all: Case[] = [...inp.incidents.map(fromIncident), ...inp.signals.map(fromSignal), ...inp.watch.map(fromWatch)];
  const onMyFloor = (x: Case) => !inp.mine || (!!x.residentId && inp.mine.has(x.residentId));
  const visible = staff ? all.filter((x) => onMyFloor(x) || isMe(x)) : all;
  const hidden = staff ? all.filter((x) => !onMyFloor(x) && !isMe(x) && x.stage !== 'closed').length : 0;

  const withClock = visible.map((x) => ({ x, c: clockOf(x, now) }));
  // Emergencies share one clock value, so between two of them the shared severity order
  // decides: 911 and self-harm or crisis first, then other critical, oldest first within.
  const emOrder = (a: Case, b: Case) => (a.kind === 'emergency' && b.kind === 'emergency' && a.emRank && b.emRank ? compareEmergencies(a.emRank, b.emRank) : 0);
  const sortedPairs = withClock.slice().sort((a, b) => a.c.left - b.c.left || emOrder(a.x, b.x));
  const sorted = sortedPairs.map((p) => p.x);
  const clk = new Map(sortedPairs.map((p) => [p.x.id, p.c]));
  const clock = (x: Case) => clk.get(x.id) ?? clockOf(x, now);

  const open = sorted.filter((x) => x.stage !== 'closed' && x.kind !== 'watch');
  const watching = sorted.filter((x) => x.kind === 'watch');
  const filt = inp.filter;
  // The hero names the most severe emergency nobody has claimed; with none unclaimed, the
  // most severe one still on its way.
  const heroOf = (pool: Case[]) =>
    pool.find((x) => x.kind === 'emergency' && x.stage === 'new') ?? pool.find((x) => x.kind === 'emergency' && x.stage !== 'closed' && x.stage !== 'onscene');
  const emTop = heroOf(sorted);
  const list = sorted
    .filter((x) => x !== emTop)
    .filter((x) => (filt === 'watch' ? x.kind === 'watch' : x.kind !== 'watch'))
    .filter((x) => (filt === 'watch' ? true : filt === 'closed' ? x.stage === 'closed' : x.stage !== 'closed'))
    .filter((x) => (filt === 'mine' ? isMe(x) : filt === 'unowned' ? !x.owner : true));
  const selId = inp.sel && sorted.find((x) => x.id === inp.sel) ? inp.sel : (list[0] ?? sorted[0])?.id ?? null;
  const em = heroOf(open) ?? null;
  const urgent = open.filter((x) => x.kind === 'emergency' || clock(x).left < 3600000);
  const unowned = open.filter((x) => !x.owner);
  const ownerText = (x: Case) => (x.owner ? (isMe(x) ? 'You' : x.owner) : 'Unowned');
  const roomText = (x: Case) => (x.type === 'HOME' ? 'At home' : `Rm ${x.room ?? '–'}`);

  const rowOf = (x: Case): Row => {
    const c = clock(x);
    const f = familyOf(x);
    const on = x.id === selId;
    const stg = STAGES[f];
    const si = stg.findIndex((z) => z[0] === x.stage);
    return {
      id: x.id,
      who: x.who,
      room: roomText(x) + (ro ? ` · ${x.bldg || inp.orgName}` : ''),
      kind: labelOf(x),
      what: x.what,
      clockText: c.text,
      clockColor: c.color,
      owner: ownerText(x),
      ownerColor: x.owner ? '#3C454F' : '#B3261E',
      stageLabel: stg[Math.max(0, si)][1],
      dots: stg.map((_z, i) => 'width:' + (i === si ? 16 : 6) + 'px;height:6px;border-radius:3px;flex:0 0 auto;background:' + (i <= si ? (i === si ? EDGE[x.kind] || '#1D3F6B' : '#54606C') : 'rgba(34,39,46,.14)') + ';'),
      hasNext: canAct(x) && x.stage !== 'closed' && x.kind !== 'watch' && stepOk(x),
      nextLabel: nextLabelOf(x),
      nextStyle: btn(staff, x.kind === 'emergency' || x.stage === 'ack' ? 'red' : ''),
      rowStyle:
        'flex:0 0 auto;display:flex;gap:0;min-width:0;border-radius:11px;cursor:pointer;overflow:hidden;box-sizing:border-box;' +
        (on ? 'background:#fff;box-shadow:0 0 0 2px #1D3F6B,0 6px 18px rgba(34,39,46,.08);' : 'background:#FCFAF4;box-shadow:0 0 0 1px rgba(34,39,46,.12);'),
      edgeStyle: 'flex:0 0 4px;background:' + (EDGE[x.kind] || '#54606C') + ';',
      barStyle: 'display:block;height:3px;border-radius:2px;background:' + c.color + ';width:' + Math.round(Math.max(0, Math.min(1, c.pct)) * 100) + '%;transition:width 1s linear;',
    };
  };

  const sel = sorted.find((x) => x.id === selId) ?? null;
  const selC = sel ? clock(sel) : null;
  const form = !!sel && inp.formFor === sel.id;
  // The headline counts people, not cases: one resident with a fall and an escalation is
  // one person who needs someone. A case with no resident id is counted by its name.
  const urgentPeople = new Set(urgent.map((x) => x.residentId ?? 'name:' + x.who)).size;
  const answer = urgent.length
    ? `${urgentPeople}${urgentPeople > 1 ? ' residents need' : ' resident needs'} someone within the hour.`
    : open.length
      ? `No one is in immediate danger. ${open.length} open.`
      : 'No one is in danger right now.';
  const firstUrgent = urgent.find((x) => x.kind !== 'emergency');
  // Everything else due within the hour besides the emergency in the card, other emergencies
  // included: "Nothing else is due" only when that is true (the tile counts them all).
  const others = urgent.filter((x) => x !== em);
  const next = others.find((x) => x.kind !== 'emergency') ?? others[0] ?? null;
  const thenText = next
    ? `Then ${next.who}, ${clock(next).text}.${others.length > 1 ? ` ${others.length - 1} more due within the hour.` : ''}`
    : 'Nothing else is due within the hour.';
  const answerSub = em
    ? `${em.who.split(' ')[0]}’s ${em.emWord || 'emergency'} first${em.stage === 'new' ? ', nobody is on it yet' : ''}. ${thenText}`
    : firstUrgent
      ? `${firstUrgent.who}: ${clock(firstUrgent).text}.`
      : unowned.length
        ? `${unowned.length} still need an owner.`
        : 'Everything open has an owner and time on its clock.';

  const tapeCol = (k: string) => (k === 'Emergency' ? '#B3261E' : k === 'Gate' ? '#8A5A00' : '#1F6B52');
  const stagesFor = sel ? STAGES[familyOf(sel)] : [];
  const selSi = sel ? stagesFor.findIndex((y) => y[0] === sel.stage) : -1;

  return {
    all,
    sorted,
    sel,
    em,
    canAct,
    scope: staff ? null : lens === 'admin' ? `${inp.orgName} · all floors` : 'All buildings · read-only',
    canReport: !ro,
    answer,
    answerColor: em || urgent.length ? '#B3261E' : '#12503C',
    answerSub,
    // The tiles count alerts (one resident can have several); the headline counts residents.
    stats: ([[open.length, 'open alerts'], [unowned.length, 'unowned alerts'], [urgent.length, 'alerts due within the hour'], [open.filter((x) => x.stage === 'investigating').length, 'investigating']] as [number, string][]).map(
      ([n, k]) => ({ v: String(n), k, c: k === 'unowned alerts' && n ? '#B3261E' : '#22272E' }),
    ),
    hidden,
    emShow: !!em,
    emWho: em ? `${em.who}, ${roomText(em)}` : '',
    emWhat: em ? em.what : '',
    // The alert's kind, as the headline names it ("Cognitive crisis"), so the card and the headline name the same alert.
    emKind: em ? (em.emWord || 'emergency').replace(/^./, (c) => c.toUpperCase()) : '',
    emClock: em ? dur(now - em.at) : '',
    emState: em ? (em.stage === 'new' ? 'Nobody has claimed it' : `${responderName(em, meId) ?? em.owner ?? 'Someone'}${em.stage === 'onscene' ? ' is with them' : ' is on the way'}`) : '',
    emCanAct: !!em && !ro,
    emNext: em ? nextLabelOf(em) : '',
    emNextOk: !!em && stepOk(em),
    filters: (
      [
        ['open', 'Open', open.length],
        ['mine', 'Mine', open.filter(isMe).length],
        ['unowned', 'Unowned', unowned.length],
        ['watch', 'Watching', watching.length],
        ['closed', 'Closed', sorted.filter((x) => x.stage === 'closed').length],
      ] as [Filter, string, number][]
    )
      .filter((z) => !(ro && z[0] === 'mine'))
      .map(([key, label, n]) => ({
        key,
        label: `${label} ${n}`,
        style:
          'display:inline-flex;align-items:center;min-height:' + (staff ? 40 : 32) + 'px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
          (filt === key ? 'color:#FCFAF4;background:#22272E;' : 'color:#3C454F;background:rgba(34,39,46,.06);'),
      })),
    rows: list.map(rowOf),
    empty: list.length === 0,
    emptyText:
      filt === 'closed' ? 'Nothing closed yet this week.' : filt === 'mine' ? 'Nothing is assigned to you.' : filt === 'unowned' ? 'Everything open has an owner.' : emTop && filt === 'open' ? 'Nothing else is open: the emergency above is the only one.' : 'No open signals. Guardian keeps watching; rows land here the moment something crosses a line.',
    tape: inp.tape.map((t) => ({ t: hhmm(Date.parse(t.at)), k: t.kind, d: t.text, dot: 'width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + tapeCol(t.kind) + ';' })),
    selMeta: sel ? `${labelOf(sel)} · ${roomText(sel)} · ${sel.bldg || inp.orgName}${sel.type === 'ALF' ? ' (assisted living)' : ''}` : '',
    selClock: selC,
    selBar: selC ? 'display:block;height:6px;border-radius:3px;background:' + selC.color + ';width:' + Math.round(Math.max(0, Math.min(1, selC.pct)) * 100) + '%;transition:width 1s linear;' : '',
    selOwner: sel ? ownerText(sel) : '',
    selOwnerColor: sel && !sel.owner ? '#B3261E' : '#22272E',
    canAssign: lens === 'admin' && !!sel && sel.stage !== 'closed',
    selStages: stagesFor.map((z, i) => ({
      label: z[1],
      s: 'flex:1 1 0;min-width:0;display:flex;flex-direction:column;gap:5px;font-size:14px;font-weight:700;line-height:1.25;color:' + (i <= selSi ? '#22272E' : '#54606C') + ';',
      bar: 'display:block;height:4px;border-radius:2px;background:' + (i < selSi ? '#54606C' : i === selSi ? (sel ? EDGE[sel.kind] || '#1D3F6B' : '#1D3F6B') : 'rgba(34,39,46,.12)') + ';',
    })),
    selHasNext: !!sel && canAct(sel) && sel.stage !== 'closed' && sel.kind !== 'watch' && !form && stepOk(sel),
    selNext: sel ? nextLabelOf(sel) : '',
    selNextStyle: sel ? btn(staff, sel.kind === 'emergency' || sel.stage === 'ack' ? 'red' : '') : '',
    selEscStyle: btn(staff, 'ghost'),
    selEscLabel: sel && sel.escalated ? 'Escalated' : 'Escalate',
    selRo: !!sel && !canAct(sel),
    formShow: form,
    selLog: sel ? sel.log.slice().sort((a, b) => b.at - a.at).map((l) => ({ t: hhmm(l.at), who: l.who, what: l.what, id: l.id })) : [],
  };
}

/**
 * The form's recipients, from the building's state (signal field reportTo). With
 * none on file the form names the kind of agency and says the state's is not loaded,
 * rather than guessing a state.
 */
export function recipientsFor(x: Case | null): [string, string, string][] {
  if (x?.reportTo?.length) return x.reportTo.map((r) => [r.key, r.label, r.sub]);
  return x && x.type === 'SNF'
    ? [['state', 'State survey agency', 'This building’s state intake is not loaded yet'], ['police', 'Local law enforcement', 'Police department for this address']]
    : [['aps', 'Adult Protective Services', 'This building’s state rule is not loaded yet']];
}
